import React from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, Link } from 'react-router-dom';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import RNavBar from './components/navigation/RNavBar';
import PortalNavBar from './components/navigation/PortalNavBar';
import MainPage from './pages/MainPage/MainPage';
import GreetingPage from './pages/SubPages/GreetingPage';
import ProgramsPage from './pages/SubPages/ProgramsPage';
import ElementaryProgramPage from './pages/SubPages/ElementaryProgramPage';
import MiddleProgramPage from './pages/SubPages/MiddleProgramPage';
import HighProgramPage from './pages/SubPages/HighProgramPage';
import AdultProgramPage from './pages/SubPages/AdultProgramPage';
import CommunityPage from './pages/SubPages/CommunityPage';
import SchedulePage from './pages/SubPages/SchedulePage';
import LocationPage from './pages/SubPages/LocationPage';
import JoinTodayPage from './pages/SubPages/JoinTodayPage';
import TypingRankingPage from './pages/SubPages/TypingRankingPage';
import PortalLoginPage from './pages/PortalPages/PortalLoginPage';
import PortalHomePage from './pages/PortalPages/PortalHomePage';
import PortalNoticesPage from './pages/PortalPages/PortalNoticesPage';
import PortalNoticeDetailPage from './pages/PortalPages/PortalNoticeDetailPage';
import PortalBoardPage from './pages/PortalPages/PortalBoardPage';
import PortalBoardDetailPage from './pages/PortalPages/PortalBoardDetailPage';
import PortalPresentationEventPage from './pages/PortalPages/PortalPresentationEventPage';
import PortalPresentationWorkPage from './pages/PortalPages/PortalPresentationWorkPage';
import PortalProfilePage from './pages/PortalPages/PortalProfilePage';
import PortalStudentPage from './pages/PortalPages/PortalStudentPage';
import PortalTeacherPage from './pages/PortalPages/PortalTeacherPage';
import PortalStaffPage from './pages/PortalPages/PortalStaffPage';
import PortalTeacherManagementPage from './pages/PortalPages/PortalTeacherManagementPage';
import PortalTypingAllRecordsPage from './pages/PortalPages/PortalTypingAllRecordsPage';
import PortalTypingRecordsPage from './pages/PortalPages/PortalTypingRecordsPage';
import PortalTypingTodayPage from './pages/PortalPages/PortalTypingTodayPage';
import PortalTypingApprovalPage from './pages/PortalPages/PortalTypingApprovalPage';
import AttendanceKioskPage from './pages/AttendancePages/AttendanceKioskPage';
import AttendanceBoardPage from './pages/AttendancePages/AttendanceBoardPage';
import RecreationPage from './pages/RecreationPages/RecreationPage';
import GlobalStyles from './styles/GlobalStyles';
import theme from './styles/theme';
import { AppWrapper, PageContent } from './styles/AppLayout';
import useScrollToTop from './hooks/useScrollToTop';
import styled from 'styled-components';

const PORTAL_USER_KEY = 'portal_user';

const ScrollToTop = ({ children }) => {
  useScrollToTop();
  return children;
};

const PortalStyles = createGlobalStyle`
  body {
    background: #020617;
  }
`;

const PortalWrapper = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top, #0f172a 0%, #020617 60%);
  display: flex;
  flex-direction: column;
`;

const PortalContent = styled.main`
  flex: 1;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 1.5rem 3rem;

  @media (max-width: 900px) {
    padding: 1rem 0.75rem 1.5rem;
  }
`;

const PortalFooter = styled.footer`
  width: 100%;
  margin: 0;
  padding: 0;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(2, 6, 23, 0.85);
`;

const PortalFooterInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  color: #94a3b8;

  @media (max-width: 900px) {
    padding: 0.85rem 0.75rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PortalFooterText = styled.p`
  margin: 0;
  font-size: 0.86rem;
`;

const PortalFooterLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  color: #e2e8f0;
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.35);
  font-size: 0.9rem;
  font-weight: 600;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    color: #fff;
    background: rgba(148, 163, 184, 0.22);
  }
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.roles)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const resolvePortalHomePath = (user) => {
  const roles = user?.roles || [];
  if (
    roles.includes('super_admin') ||
    roles.includes('admin') ||
    roles.includes('teacher') ||
    roles.includes('student') ||
    roles.includes('parent')
  ) {
    return '/portal/home';
  }
  return '/portal/login';
};

const ProtectedPortalRoute = ({ component: Component, allowedRoles, ...rest }) => (
  <Route
    {...rest}
    render={(props) => {
      const portalUser = readPortalUser();
      if (!portalUser) {
        return <Redirect to="/portal/login" />;
      }
      const hasRole = allowedRoles.some((role) => portalUser.roles.includes(role));
      if (!hasRole) {
        return <Redirect to={resolvePortalHomePath(portalUser)} />;
      }
      return <Component {...props} />;
    }}
  />
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <GlobalStyles />
        <ScrollToTop>
          <Switch>
            <Route path="/attendance/board" exact>
              <Redirect to="/portal/teacher/attendance-records" />
            </Route>
            <Route path="/attendance" exact component={AttendanceKioskPage} />
            <Route path="/portal/recreation" component={RecreationPage} />
            <Route
              path="/recreation"
              render={({ location }) => (
                <Redirect to={`/portal${location.pathname}${location.search}${location.hash}`} />
              )}
            />
            <Route path="/portal">
              <PortalStyles />
              <PortalWrapper>
                <PortalNavBar />
                <PortalContent>
                  <Switch>
                    <Route path="/portal" exact>
                      <Redirect to="/portal/login" />
                    </Route>
                    <Route path="/portal/login" component={PortalLoginPage} />
                    <ProtectedPortalRoute
                      path="/portal/home"
                      component={PortalHomePage}
                      allowedRoles={['student', 'parent', 'teacher', 'admin', 'super_admin']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/notices/:noticeId"
                      component={PortalNoticeDetailPage}
                      allowedRoles={['student', 'parent', 'teacher', 'admin', 'super_admin']}
                    />
                    <ProtectedPortalRoute
                      exact
                      path="/portal/notices"
                      component={PortalNoticesPage}
                      allowedRoles={['student', 'parent', 'teacher', 'admin', 'super_admin']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/board/:postId"
                      component={PortalBoardDetailPage}
                      allowedRoles={['student', 'parent', 'teacher', 'admin', 'super_admin']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/board"
                      component={PortalBoardPage}
                      allowedRoles={['student', 'parent', 'teacher', 'admin', 'super_admin']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/presentations/:eventId/works/:workId"
                      component={PortalPresentationWorkPage}
                      allowedRoles={['student', 'parent', 'teacher', 'admin', 'super_admin']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/presentations/year/:eventYear"
                      component={PortalPresentationEventPage}
                      allowedRoles={['student', 'parent', 'teacher', 'admin', 'super_admin']}
                    />
                    <Route path="/portal/presentations/:eventId" exact>
                      <Redirect to="/portal/presentations/year/2026" />
                    </Route>
                    <Route path="/portal/presentations" exact>
                      <Redirect to="/portal/presentations/year/2026" />
                    </Route>
                    <ProtectedPortalRoute
                      path="/portal/profile"
                      component={PortalProfilePage}
                      allowedRoles={['student', 'parent', 'teacher', 'admin', 'super_admin']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/student"
                      component={PortalStudentPage}
                      allowedRoles={['student', 'parent']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/typing/all"
                      component={PortalTypingAllRecordsPage}
                      allowedRoles={['student']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/typing/my"
                      component={PortalTypingRecordsPage}
                      allowedRoles={['student']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/typing/today"
                      component={PortalTypingTodayPage}
                      allowedRoles={['student']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/teacher/typing-approvals"
                      component={PortalTypingApprovalPage}
                      allowedRoles={['teacher', 'admin', 'super_admin']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/teacher/attendance-records"
                      component={AttendanceBoardPage}
                      allowedRoles={['teacher', 'admin', 'super_admin']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/teacher"
                      component={PortalTeacherPage}
                      allowedRoles={['teacher']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/staff/teachers"
                      component={PortalTeacherManagementPage}
                      allowedRoles={['admin', 'super_admin']}
                    />
                    <ProtectedPortalRoute
                      path="/portal/staff"
                      component={PortalStaffPage}
                      allowedRoles={['admin', 'super_admin']}
                    />
                  </Switch>
                </PortalContent>
                <PortalFooter>
                  <PortalFooterInner>
                    <PortalFooterText>AceCoding Portal 2026</PortalFooterText>
                    <PortalFooterLink to="/">홈페이지 바로가기</PortalFooterLink>
                  </PortalFooterInner>
                </PortalFooter>
              </PortalWrapper>
            </Route>
            <Route>
              <AppWrapper>
                <RNavBar />
                <PageContent>
                  <Switch>
                    <Route path="/" exact component={MainPage} />
                    <Route path="/greeting" component={GreetingPage} />
                    <Route path="/programs" exact component={ProgramsPage} />
                    <Route path="/programs/elementary" component={ElementaryProgramPage} />
                    <Route path="/programs/middle" component={MiddleProgramPage} />
                    <Route path="/programs/high" component={HighProgramPage} />
                    <Route path="/programs/adult" component={AdultProgramPage} />
                    <Route path="/community" component={CommunityPage} />
                    <Route path="/schedule" component={SchedulePage} />
                    <Route path="/location" component={LocationPage} />
                    <Route path="/join" component={JoinTodayPage} />
                    <Route path="/ranking" component={TypingRankingPage} />
                  </Switch>
                </PageContent>
              </AppWrapper>
            </Route>
          </Switch>
        </ScrollToTop>
      </Router>
    </ThemeProvider>
  );
}

export default App;
